import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * Outbound event subscriptions — the `/v1/subscriptions/**` surface (OPSFL-CLIENT-PLATFORM P2,
 * slices S1+S2; `product-api/.../web/WebhookController.java` is the wire truth). Keys:
 * `subscriptions.read` (hub_admin, hub_operator, auditor), `subscriptions.write` (hub_admin,
 * STEP_UP — it mints a signing secret and redirects tenant data outward),
 * `subscriptions.deliveries.read` / `subscriptions.deliveries.replay`. The raw `whsec_` secret
 * rides ONLY the create/rotate response (`SecretGrant`) — it is derived server-side, never at
 * rest, never re-readable. Mutations carry `ol-idempotency-key`.
 */

export type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'AUTO_DISABLED';
export type DeliveryState = 'PENDING' | 'RETRYING' | 'DELIVERED' | 'DEAD' | 'SKIPPED';

/** Server facts mirrored for display copy only (FSD §4.4) — the server enforces them. */
export const RETRY_LADDER = ['1m', '5m', '30m', '2h', '24h'] as const;
export const AUTO_DISABLE_THRESHOLD = 100;
export const MAX_SUBSCRIPTIONS_PER_HUB = 10;

export interface SubscriptionView {
  subscriptionId: string;
  name: string;
  targetUrl: string;
  scope: string;
  eventTypes: string[];
  status: SubscriptionStatus | string;
  secretVersion: number;
  /** sha256 fingerprint PREFIX (12 hex chars) for support — never the secret. */
  secretFingerprint: string;
  consecutiveFailures: number;
  apiVersion: string;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SubscriptionList {
  hub: string;
  subscriptions: SubscriptionView[];
  /** The CLOSED event catalogue, server-sorted — the only types a subscription may name. */
  subscribableEventTypes: string[];
}

/** Create/rotate response: the subscription plus the ONE-TIME raw `whsec_` secret. */
export interface SecretGrant {
  subscription: SubscriptionView;
  secret: string;
  secretNote: string | null;
}

/** Result of the synchronous signed `/test` ping (a ping never retries: DELIVERED or DEAD). */
export interface TestDelivery {
  deliveryId: string;
  eventType: string;
  state: DeliveryState | string;
  statusCode: number | null;
  error: string | null;
}

/** One delivery-ledger row (S2) — append-then-update, never deleted. */
export interface DeliveryRow {
  deliveryId: string;
  eventId: string;
  eventType: string;
  state: DeliveryState | string;
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  firstAttemptAt: string | null;
  deliveredAt: string | null;
  /** Set when this row is an operator replay of a DEAD one. */
  replayOf: string | null;
  createdAt: string | null;
}

/** Receipt for a replay: the NEW row queued outside the dedup index. */
export interface ReplayReceipt {
  deliveryId: string;
  replayOf: string;
  state: string;
}

export interface CreateSubscriptionRequest {
  name: string;
  targetUrl: string;
  eventTypes: string[];
}

/** PUT body — the server COALESCEs: an absent/blank field keeps the stored value. */
export interface UpdateSubscriptionRequest {
  name?: string;
  targetUrl?: string;
  eventTypes?: string[];
}

// ---- pure response mapping (unit-tested; the UI never trusts a shape blindly) -----------------

/** Normalise the list envelope body — absent arrays become empty, never undefined. */
export function asSubscriptionList(body: unknown): SubscriptionList {
  const b = (body && typeof body === 'object' ? body : {}) as Partial<SubscriptionList>;
  return {
    hub: typeof b.hub === 'string' ? b.hub : '',
    subscriptions: Array.isArray(b.subscriptions) ? b.subscriptions : [],
    subscribableEventTypes: Array.isArray(b.subscribableEventTypes)
      ? b.subscribableEventTypes.filter((t): t is string => typeof t === 'string')
      : [],
  };
}

/** Pull the rows out of the deliveries envelope (`{subscriptionId, deliveries: [...]}`). */
export function asDeliveryRows(body: unknown): DeliveryRow[] {
  const b = (body && typeof body === 'object' ? body : {}) as { deliveries?: unknown };
  return Array.isArray(b.deliveries) ? (b.deliveries as DeliveryRow[]) : [];
}

// ---- the wire calls ---------------------------------------------------------------------------

/** GET /v1/subscriptions — subscriptions + the closed catalogue. Key: subscriptions.read. */
export async function listSubscriptions(): Promise<SubscriptionList> {
  const { data } = await apiClient.get<ApiEnvelope<SubscriptionList>>('/v1/subscriptions');
  return asSubscriptionList(unwrap(data));
}

/** GET /v1/subscriptions/{id}. Key: subscriptions.read. */
export async function getSubscription(subscriptionId: string): Promise<SubscriptionView> {
  const { data } = await apiClient.get<ApiEnvelope<SubscriptionView>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}`,
  );
  return unwrap(data);
}

/**
 * POST /v1/subscriptions — key: subscriptions.write (STEP_UP). Returns the ONE-TIME secret.
 * A retried create with the same idempotency key returns the ORIGINAL response (which carried
 * the secret once); nothing new is minted.
 */
export async function createSubscription(req: CreateSubscriptionRequest): Promise<SecretGrant> {
  const { data } = await apiClient.post<ApiEnvelope<SecretGrant>>(
    '/v1/subscriptions',
    req,
    idempotentConfig(),
  );
  return unwrap(data);
}

/** PUT /v1/subscriptions/{id} — partial update (server keeps blank fields). Key: subscriptions.write. */
export async function updateSubscription(
  subscriptionId: string,
  req: UpdateSubscriptionRequest,
): Promise<SubscriptionView> {
  const { data } = await apiClient.put<ApiEnvelope<SubscriptionView>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}`,
    req,
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * POST /v1/subscriptions/{id}/rotate-secret — key: subscriptions.write (STEP_UP). The previous
 * secret verifies deliveries through a 24h overlap window (X-OL-Signature-Prev), then dies.
 */
export async function rotateSubscriptionSecret(subscriptionId: string): Promise<SecretGrant> {
  const { data } = await apiClient.post<ApiEnvelope<SecretGrant>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/rotate-secret`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}

/** POST /v1/subscriptions/{id}/pause — legal from ACTIVE or AUTO_DISABLED. Key: subscriptions.write. */
export async function pauseSubscription(subscriptionId: string): Promise<SubscriptionView> {
  const { data } = await apiClient.post<ApiEnvelope<SubscriptionView>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/pause`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * POST /v1/subscriptions/{id}/resume — legal from PAUSED or AUTO_DISABLED; resets the
 * consecutive-failure counter. Queued deliveries flow again on the next sweep.
 */
export async function resumeSubscription(subscriptionId: string): Promise<SubscriptionView> {
  const { data } = await apiClient.post<ApiEnvelope<SubscriptionView>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/resume`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * POST /v1/subscriptions/{id}/test — synchronous signed ping (`product.subscription.ping.v1`).
 * Only an ACTIVE subscription pings (409 WEBHOOK_STATE_CONFLICT otherwise). Key: subscriptions.write.
 */
export async function testSubscription(subscriptionId: string): Promise<TestDelivery> {
  const { data } = await apiClient.post<ApiEnvelope<TestDelivery>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/test`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}

/**
 * GET /v1/subscriptions/{id}/deliveries — the ledger, newest first, optional state filter
 * (server caps limit at 200). Key: subscriptions.deliveries.read.
 */
export async function listDeliveries(
  subscriptionId: string,
  state?: DeliveryState | '',
  limit = 50,
): Promise<DeliveryRow[]> {
  const { data } = await apiClient.get<ApiEnvelope<unknown>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/deliveries`,
    { params: { ...(state ? { state } : {}), limit } },
  );
  return asDeliveryRows(unwrap(data));
}

/**
 * POST /v1/subscriptions/{id}/deliveries/{deliveryId}/replay — a NEW ledger row outside the
 * dedup index; only DEAD rows replay (409 otherwise). Key: subscriptions.deliveries.replay.
 */
export async function replayDelivery(
  subscriptionId: string,
  deliveryId: string,
): Promise<ReplayReceipt> {
  const { data } = await apiClient.post<ApiEnvelope<ReplayReceipt>>(
    `/v1/subscriptions/${encodeURIComponent(subscriptionId)}/deliveries/${encodeURIComponent(deliveryId)}/replay`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}
